import { useCallback, useEffect, useState } from 'react';

import { type OnPerformanceTrackingEvent } from './types';

export const usePerformanceTracking = ({
	onEvent,
	initialData,
}: {
	onEvent?: OnPerformanceTrackingEvent;
	initialData: Promise<unknown> | undefined;
}): {
	onMount: () => () => void | undefined;
	isLoading: boolean;
	onComponentError: (error: Error) => void;
} => {
	// Initialise to true only if there's a Promise to await, avoiding an unnecessary UFO hold on mount.
	const [isLoading, setIsLoading] = useState(initialData !== undefined);

	const onMount = useCallback((): (() => void | undefined) => {
		return () => onEvent?.({ type: 'unmount' });
	}, [onEvent]);

	useEffect(() => {
		if (initialData) {
			void initialData
				.then(() => {
					onEvent?.({ type: 'initial-data-complete' });
				})
				.catch((error) => {
					onEvent?.({ type: 'initial-data-error', error });
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			setIsLoading(false);
		}
	}, [initialData, onEvent]);

	const onComponentError = useCallback(
		(error: Error): void => {
			onEvent?.({ type: 'component-error', error });
		},
		[onEvent],
	);

	useEffect(() => {
		if (isLoading === false) {
			onEvent?.({ type: 'component-ready' });
		}
	}, [isLoading]);

	return {
		onMount,
		isLoading,
		onComponentError,
	};
};
